import { useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@stores/useAuthStore';
import { queryKeys } from '@lib/queryClient';
import { useShallow } from 'zustand/react/shallow';

const TERMINAL_KYC_NOTIFICATION_REFRESH_DELAYS_MS = [0, 1500, 5000] as const;

export function scheduleKycNotificationRefreshes({
    queryClient,
    userId,
    normalizedStatus,
    setTimeoutFn = globalThis.setTimeout,
}: {
    queryClient: Pick<QueryClient, 'invalidateQueries'>;
    userId: string;
    normalizedStatus: string;
    setTimeoutFn?: typeof globalThis.setTimeout;
}) {
    const delays =
        normalizedStatus === 'approved' || normalizedStatus === 'declined'
            ? TERMINAL_KYC_NOTIFICATION_REFRESH_DELAYS_MS
            : [0];

    return delays.map((delayMs) =>
        setTimeoutFn(() => {
            void queryClient.invalidateQueries({
                queryKey: queryKeys.notifications(userId),
            });
        }, delayMs)
    );
}

/**
 * Handles return traffic from KYC providers via URL params.
 * Place after auth so it can refresh the profile, show the appropriate toast, and strip callback params from the URL.
 */
export function useKYCCallbackEffect() {
    const queryClient = useQueryClient();
    const {
        authUser,
        isAuthenticated,
        refreshUserProfile,
    } = useAuthStore(useShallow(s => ({
        authUser: s.authUser,
        isAuthenticated: s.isAuthenticated,
        refreshUserProfile: s.refreshUserProfile,
    })));

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const params = new URLSearchParams(window.location.search);
        const verificationSessionId = params.get('verificationSessionId');
        const statusParam = params.get('status');

        if (!verificationSessionId && !statusParam) {
            return;
        }

        let isActive = true;
        const notificationRefreshTimers: ReturnType<typeof setTimeout>[] = [];

        const cleanParams = () => {
            params.delete('verificationSessionId');
            params.delete('status');
            const remainingSearch = params.toString();
            const cleanUrl = remainingSearch
                ? `${window.location.pathname}?${remainingSearch}`
                : window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
        };

        if (!isAuthenticated) {
            cleanParams();
            return;
        }

        (async () => {
            try {
                await refreshUserProfile();

                if (!isActive) {
                    return;
                }

                const normalizedStatus = (statusParam ?? '').toLowerCase();

                if (authUser?.uid) {
                    notificationRefreshTimers.push(
                        ...scheduleKycNotificationRefreshes({
                            queryClient,
                            userId: authUser.uid,
                            normalizedStatus,
                        }),
                    );
                }

                if (normalizedStatus === 'approved') {
                    toast.success('✅ Identity verified', {
                        description: 'Thanks for proving you’re human. Rewards stay fair for everyone.',
                    });
                } else if (normalizedStatus === 'declined') {
                    //TODO test if works as expected
                    toast.error('Verification declined', {
                        description: 'We could not verify you this time. Please try again or contact support.',
                    });
                } else if (normalizedStatus === 'in review' || normalizedStatus === 'in_review') {
                    toast.info('Verification under review', {
                        description: 'We are reviewing your submission. You can continue exploring meanwhile.',
                    });
                }
            } catch (error) {
                console.error('Failed to refresh after KYC callback:', error);
                if (isActive) {
                    toast.error('We could not refresh your verification status. Please try again.');
                }
            } finally {
                if (isActive) {
                    cleanParams();
                }
            }
        })();

        return () => {
            isActive = false;
            notificationRefreshTimers.forEach((timer) => clearTimeout(timer));
        };
    }, [authUser?.uid, isAuthenticated, queryClient, refreshUserProfile]);
}
